import React, { useState, useEffect, useCallback } from 'react';
import { Table, Input, FormGroup, Col, Row, Button, InputGroup, InputGroupAddon } from 'reactstrap';
import Pagination from 'rc-pagination';
import SetLimitEntries from './SetLimitEntries';
import { ENG_LOCALE } from '../lib/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useUnifiedFetch } from '../hooks/useUnifiedFetch';

const INITIAL_SORT = { field: 'created_at', direction: 'desc' };
const INITIAL_PAGE_SIZE = 10;

const NocHistory = ({ boxDetails }) => {
  const [data, setData] = useState({ docs: [], totalDocs: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState(INITIAL_SORT);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: INITIAL_PAGE_SIZE,
  });
  const [errorMessage, setErrorMessage] = useState('');

  const endpoint = boxDetails?._id ? `boxes/${boxDetails._id}/teamsEscalation` : null;
  const { isLoading, fetchData, error } = useUnifiedFetch(endpoint, false);

  const loadData = useCallback(async () => {
    if (!boxDetails?._id) return;

    try {
      setErrorMessage('');
      const queryFilter = searchTerm
        ? {
            $or: [
              { type: { $regex: searchTerm, $options: 'i' } },
              { text: { $regex: searchTerm, $options: 'i' } },
              { cwTicket: { $regex: searchTerm, $options: 'i' } },
            ],
          }
        : {};

      const sortQuery = { [sort.field]: sort.direction === 'asc' ? 1 : -1 };
      const paginateOptions = {
        page: pagination.current,
        limit: pagination.pageSize,
      };

      const response = await fetchData({
        queryFilter: JSON.stringify(queryFilter),
        sort: JSON.stringify(sortQuery),
        paginateOptions: JSON.stringify(paginateOptions),
      });

      if (!response || response.status === false) {
        setErrorMessage(response?.message || 'Failed to load NOC history');
        setData({ docs: [], totalDocs: 0 });
        return;
      }

      if (Array.isArray(response.docs)) {
        setData(response);
      } else {
        setErrorMessage('Invalid data format received');
        setData({ docs: [], totalDocs: 0 });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setErrorMessage('An unexpected error occurred while loading the data');
      setData({ docs: [], totalDocs: 0 });
    }
  }, [boxDetails?._id, searchTerm, sort, pagination, fetchData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = e => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleSort = field => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = field => {
    if (sort.field !== field) return <FontAwesomeIcon icon={faSort} />;
    return sort.direction === 'asc' ? (
      <FontAwesomeIcon icon={faSortUp} />
    ) : (
      <FontAwesomeIcon icon={faSortDown} />
    );
  };

  const handleChangePagination = page => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handleSetLimit = limit => {
    setPagination({ current: 1, pageSize: parseInt(limit, 10) });
  };
  return (
    <div>
      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          {errorMessage}
        </div>
      )}
      
      <Row className="mb-3">
        <Col md={6}>
          <FormGroup>
            <InputGroup>
              <Input
                type="text"
                placeholder="Search by type, description, or ticket..."
                value={searchTerm}
                onChange={handleSearch}
              />
              <InputGroupAddon addonType="append">
                <Button color="secondary">
                  <FontAwesomeIcon icon={faSearch} />
                </Button>
              </InputGroupAddon>
            </InputGroup>
          </FormGroup>
        </Col>
      </Row>{' '}
      <Table responsive striped>
        <thead>
          <tr>
          <th onClick={() => handleSort('cwTicket')} style={{ cursor: 'pointer' , whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
          CW Ticket {getSortIcon('cwTicket')}
            </th>
            <th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>
              Type {getSortIcon('type')}
            </th>
            <th onClick={() => handleSort('text')} style={{ cursor: 'pointer' }}>
              Description {getSortIcon('text')}
            </th>
            <th>Link</th>
            <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
              Created {getSortIcon('created_at')}
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan="6" className="text-center">
                Loading...
              </td>
            </tr>
          ) : errorMessage ? (
            <tr>
              <td colSpan="6" className="text-center text-danger">
                {errorMessage}
              </td>
            </tr>
          ) : data.docs.length === 0 ? (
            <tr>
              <td colSpan="6" className="text-center">
                No records found
              </td>
            </tr>
          ) : (
            data.docs.map(item => (
              <tr key={item._id}>
                <td>{item.cwTicket}</td>
                <td>{item.type}</td>
                <td style={{ whiteSpace: 'pre-line' }}>{item.text}</td>
                <td>
                  {item?.link && (
                    <a href={item.link} target="_blank" rel="noopener noreferrer">
                      View Ticket
                    </a>
                  )}
                </td>
                <td>{new Date(item.created_at).toLocaleString()}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
      
      {!errorMessage && data.docs.length > 0 && (
        <Row className="justify-content-between align-items-center mt-3">
          <Col xs="auto">
            <SetLimitEntries value={pagination.pageSize} onChange={handleSetLimit} />
          </Col>
          <Col xs="auto">
            <Pagination
              locale={ENG_LOCALE}
              className="pagination-container"
              onChange={handleChangePagination}
              current={pagination.current}
              total={data.totalDocs}
              pageSize={pagination.pageSize}
              showLessItems
              showTotal={(total, range) => `${range[0]} - ${range[1]} of ${total} items`}
            />
          </Col>
        </Row>
      )}
    </div>
  );
};

export default NocHistory;
